import { Helmet } from 'react-helmet-async';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Card,
  Stack,
  Button,
  Container,
  Typography,
  TextField,
  Switch,
  FormControlLabel,
  Grid,
  Chip,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  OutlinedInput,
  Box,
} from '@mui/material';
import { collection, doc, getDoc, getDocs, setDoc, updateDoc, getFirestore, serverTimestamp } from 'firebase/firestore';
import firebaseApp from '../utils/firebaseConfig';

const db = getFirestore(firebaseApp);

export default function NewLanePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('id');

  const [formData, setFormData] = useState({
    name: '',
    categoryIds: [],
    capacity: 1,
    active: true,
    sortOrder: 0,
  });
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchCategories();
    if (editId) loadLane();
  }, [editId]);

  const fetchCategories = async () => {
    const snap = await getDocs(collection(db, 'reservation_categories'));
    setCategories(snap.docs.map((d) => ({ ...d.data(), id: d.id })));
  };

  const loadLane = async () => {
    try {
      const docSnap = await getDoc(doc(db, 'lanes', editId));
      if (docSnap.exists()) setFormData({ ...formData, ...docSnap.data() });
    } catch (error) {
      console.error('Error loading lane:', error);
    }
  };

  const handleSubmit = async () => {
    if (!formData.name) { alert('Name is required'); return; }
    setLoading(true);
    try {
      if (editId) {
        await updateDoc(doc(db, 'lanes', editId), { ...formData, updatedAt: serverTimestamp() });
        alert('Lane updated successfully');
      } else {
        const newRef = doc(collection(db, 'lanes'));
        await setDoc(newRef, { ...formData, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
        alert('Lane created successfully');
      }
      navigate('/dashboard/lanes');
    } catch (error) {
      console.error('Error saving lane:', error);
      alert('Error saving lane');
    }
    setLoading(false);
  };

  return (
    <>
      <Helmet>
        <title>{editId ? 'Edit' : 'New'} Lane | BBTC Dashboard</title>
      </Helmet>

      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>{editId ? 'Edit' : 'New'} Lane</Typography>
        </Stack>

        <Card sx={{ p: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Categories</InputLabel>
                <Select
                  multiple
                  value={formData.categoryIds}
                  onChange={(e) => setFormData({ ...formData, categoryIds: e.target.value })}
                  input={<OutlinedInput label="Categories" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((id) => {
                        const cat = categories.find((c) => c.id === id);
                        return <Chip key={id} label={cat ? cat.name : id} size="small" />;
                      })}
                    </Box>
                  )}
                >
                  {categories.filter((c) => c.type === 'lane').map((cat) => (
                    <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField fullWidth type="number" label="Capacity" value={formData.capacity} onChange={(e) => setFormData({ ...formData, capacity: Number(e.target.value) || 1 })} />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField fullWidth type="number" label="Sort Order" value={formData.sortOrder} onChange={(e) => setFormData({ ...formData, sortOrder: Number(e.target.value) || 0 })} />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={<Switch checked={formData.active} onChange={(e) => setFormData({ ...formData, active: e.target.checked })} />}
                label="Active"
              />
            </Grid>
          </Grid>

          <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
            <Button variant="contained" size="large" onClick={handleSubmit} disabled={loading}>
              {loading ? 'Saving...' : (editId ? 'Update Lane' : 'Create Lane')}
            </Button>
            <Button variant="outlined" size="large" onClick={() => navigate('/dashboard/lanes')}>
              Cancel
            </Button>
          </Stack>
        </Card>
      </Container>
    </>
  );
}
